import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InboxOutlined } from '@ant-design/icons';
import styled from 'styled-components';

interface DragDropOverlayProps {
  visible: boolean;
  onClose: () => void;
}

const OverlayContainer = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.7);
  z-index: 9999; /* Verhoogde z-index om boven de zoom knoppen te komen */
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
  backdrop-filter: blur(4px);
`;

const DropZone = styled.div`
  width: 450px;
  height: 250px;
  background-color: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  padding: 24px;
  text-align: center;
`;

const IconWrapper = styled.div`
  font-size: 64px;
  color: #1890ff;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: #f0f0f0;
  }
`;

const DragDropOverlay: React.FC<DragDropOverlayProps> = ({ visible, onClose }) => {
  const { t } = useTranslation();

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) {
        onClose();
      }
    };

    // Add event listener for keyboard events
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClose]);

  // Safety timeout - als drag events verloren gaan, detecteer dat er niets wordt gesleept
  useEffect(() => {
    if (visible) {
      // Safety timeout - sluit overlay na 30 seconden
      const timeoutId = setTimeout(() => {
        onClose();
      }, 30000);

      // Extra check op mouse beweging en geen drag
      const checkMouseMovement = (e: MouseEvent) => {
        // Als de muis beweegt maar geen dataTransfer actief is, sluit de overlay
        if (!e.buttons && !window.getSelection()?.toString()) {
          // Mouse beweegt zonder drag, dus waarschijnlijk is drag beëindigd
          setTimeout(() => {
            // Dubbele check met uitstel om zeker te zijn
            if (!document.querySelector('[dragging="true"]')) {
              onClose();
            }
          }, 200);
        }
      };

      window.addEventListener('mousemove', checkMouseMovement);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('mousemove', checkMouseMovement);
      };
    }
  }, [visible, onClose]);

  // If not visible, don't render anything
  if (!visible) {
    return null;
  }

  // Handle background click to close
  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <OverlayContainer
      visible={visible}
      onClick={handleBackgroundClick}
      onDragLeave={(e) => {
        // If dragging outside the overlay, close it
        if (e.currentTarget === e.target) {
          onClose();
        }
      }}
    >
      <CloseButton onClick={onClose}>✕</CloseButton>
      <DropZone>
        <IconWrapper>
          <InboxOutlined />
        </IconWrapper>
        <Title>{t('Drop files to upload')}</Title>
        <Subtitle>{t('Drag and drop files anywhere on the page')}</Subtitle>
      </DropZone>
    </OverlayContainer>
  );
};

export default DragDropOverlay;
